import { useEffect } from "react";

/**
 * 모달 등 특정 요소의 외부 클릭 시 해당 요소 닫히는 훅 + ESC 키 지원
 *
 * @param refs - 감지할 요소의 ref 배열
 * @param onOutsideClick - 외부 클릭 or ESC 누를 때 실행할 함수
 */
export function useClickOutsideCloseModal(
  refs: React.RefObject<HTMLElement>[],
  onOutsideClick: () => void
) {
  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      // 이벤트 타겟이 모달 내부인지 확인
      const isInsideAnyModal = refs.some(
        (ref) => ref.current && ref.current.contains(event.target as Node)
      );

      // 모달 외부를 클릭한 경우에만 처리
      if (!isInsideAnyModal) {
        // 현재 모달의 z-index 확인
        const currentModalZIndex = Math.max(
          ...refs
            .map((ref) =>
              ref.current
                ? parseInt(window.getComputedStyle(ref.current).zIndex, 10)
                : 0
            )
            .filter((zIndex) => !isNaN(zIndex))
        );

        // 더 높은 z-index를 가진 모달이 있는지 확인
        const higherModalExists = Array.from(
          document.querySelectorAll('[role="dialog"]')
        ).some((modal) => {
          const modalZIndex = parseInt(
            window.getComputedStyle(modal).zIndex,
            10
          );
          return !isNaN(modalZIndex) && modalZIndex > currentModalZIndex;
        });

        // 더 높은 z-index를 가진 모달이 없을 때만 닫기
        if (!higherModalExists) {
          event.stopPropagation();
          onOutsideClick();
        }
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onOutsideClick();
      }
    };

    // 캡처링 단계에서 이벤트 처리
    document.addEventListener("mousedown", handleMouseDown, true);
    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("mousedown", handleMouseDown, true);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [refs, onOutsideClick]);
}
